import React from 'react'
import { Alert, Button, Form, Input, InputNumber, Modal, Upload, message } from 'antd'
import type { UploadFile } from 'antd/es/upload/interface'
import { InboxOutlined, SearchOutlined } from '@ant-design/icons'
import { request } from '../utils/request'
import { useAuthStore } from '../stores/auth'

type AuthorClaimModalProps = {
  open: boolean
  onClose: () => void
}

type BookLookupResponse = {
  id: number
  name: string
  author: string
  status: number
}

const { Dragger } = Upload

const buildUploadProps = (
  files: UploadFile[],
  setFiles: React.Dispatch<React.SetStateAction<UploadFile[]>>
) => ({
  multiple: true,
  beforeUpload: (file: File) => {
    setFiles((prev) => {
      const next = [...prev, file as unknown as UploadFile]
      return next.slice(0, 5)
    })
    return false
  },
  onRemove: (file: UploadFile) => {
    setFiles((prev) => prev.filter((item) => item.uid !== file.uid))
    return true
  },
  fileList: files,
  itemRender: (_originNode: React.ReactElement, file: UploadFile) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '4px 0' }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
      <Button
        type="link"
        size="small"
        danger
        onClick={() => {
          setFiles((prev) => prev.filter((item) => item.uid !== file.uid))
        }}
      >
        删除
      </Button>
    </div>
  )
})

const AuthorClaimModal: React.FC<AuthorClaimModalProps> = ({ open, onClose }) => {
  const [form] = Form.useForm()
  const { user } = useAuthStore()
  const [submitting, setSubmitting] = React.useState(false)
  const [bookLoading, setBookLoading] = React.useState(false)
  const [bookLookup, setBookLookup] = React.useState<BookLookupResponse | null>(null)
  const [identityFiles, setIdentityFiles] = React.useState<UploadFile[]>([])
  const [manuscriptFiles, setManuscriptFiles] = React.useState<UploadFile[]>([])
  const [copyrightFiles, setCopyrightFiles] = React.useState<UploadFile[]>([])
  const [contractFiles, setContractFiles] = React.useState<UploadFile[]>([])

  React.useEffect(() => {
    if (!open) return
    form.setFieldsValue({
      author_email: user?.email || '',
      author_phone: user?.phone || '',
      author_pen_name: '',
      author_real_name: '',
      author_message: '',
      book_id: undefined,
      book_name: ''
    })
    setBookLookup(null)
    setIdentityFiles([])
    setManuscriptFiles([])
    setCopyrightFiles([])
    setContractFiles([])
  }, [form, open, user?.email, user?.phone])

  const lookupBook = React.useCallback(async () => {
    const bookId = Number(form.getFieldValue('book_id') || 0)
    if (!(bookId > 0)) {
      setBookLookup(null)
      return
    }
    try {
      setBookLoading(true)
      const res = await request.get(`/api/author-claims/books/${bookId}`, {
        headers: { 'X-Skip-401-Redirect': '1', 'X-Silent': '1' }
      })
      const data = (res.data?.data || {}) as BookLookupResponse
      setBookLookup(data)
      form.setFieldsValue({
        book_name: data.name || ''
      })
    } catch (error) {
      setBookLookup(null)
      message.error((error as { response?: { data?: { message?: string } } })?.response?.data?.message || '未找到该书籍')
    } finally {
      setBookLoading(false)
    }
  }, [form])

  const handleSubmit = React.useCallback(async () => {
    try {
      const values = await form.validateFields()
      if (identityFiles.length === 0 || manuscriptFiles.length === 0 || copyrightFiles.length === 0 || contractFiles.length === 0) {
        message.error('请按要求上传 4 项必交证明材料')
        return
      }
      const fd = new FormData()
      fd.append('book_id', String(values.book_id))
      fd.append('book_name', String(values.book_name || ''))
      fd.append('author_real_name', String(values.author_real_name || ''))
      fd.append('author_pen_name', String(values.author_pen_name || ''))
      fd.append('author_email', String(values.author_email || ''))
      fd.append('author_phone', String(values.author_phone || ''))
      fd.append('author_message', String(values.author_message || ''))

      for (const file of identityFiles) {
        if (file.originFileObj) fd.append('identity_files', file.originFileObj)
      }
      for (const file of manuscriptFiles) {
        if (file.originFileObj) fd.append('manuscript_files', file.originFileObj)
      }
      for (const file of copyrightFiles) {
        if (file.originFileObj) fd.append('copyright_page_files', file.originFileObj)
      }
      for (const file of contractFiles) {
        if (file.originFileObj) fd.append('contract_files', file.originFileObj)
      }

      setSubmitting(true)
      const res = await request.post('/api/author-claims/submit', fd, {
        headers: {
          'X-Skip-401-Redirect': '1',
          'X-No-Retry': '1'
        }
      })
      message.success(res.data?.message || '作者申报已提交')
      onClose()
    } catch (error) {
      const serverMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
      if (serverMessage) {
        message.error(serverMessage)
      }
    } finally {
      setSubmitting(false)
    }
  }, [contractFiles, copyrightFiles, form, identityFiles, manuscriptFiles, onClose])

  return (
    <Modal
      title="作者申报"
      open={open}
      onCancel={onClose}
      onOk={() => { void handleSubmit() }}
      confirmLoading={submitting}
      width="min(960px, calc(100vw - 24px))"
      okText="提交申报"
      cancelText="取消"
      styles={{
        content: { maxHeight: 'calc(100vh - 24px)' },
        body: { maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }
      }}
    >
      <Alert
        showIcon
        type="info"
        style={{ marginBottom: 16 }}
        message="作者申报说明"
        description="请按要求填写书籍ID、书名、作者信息，并上传 4 项必交证明材料。系统落库后会直接发送给后台配置的两位法务邮箱进行核验。"
      />

      <Form form={form} layout="vertical">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, alignItems: 'end' }}>
          <Form.Item
            name="book_id"
            label="书籍ID"
            rules={[{ required: true, message: '请输入书籍ID' }]}
            style={{ marginBottom: 12 }}
          >
            <InputNumber min={1} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="book_name"
            label="书名"
            rules={[{ required: true, message: '请输入书名' }]}
            style={{ marginBottom: 12 }}
          >
            <Input placeholder="请输入书名" />
          </Form.Item>
          <Button icon={<SearchOutlined />} loading={bookLoading} onClick={() => { void lookupBook() }} style={{ marginBottom: 12 }}>
            校验书籍
          </Button>
        </div>

        {bookLookup ? (
          <Alert
            showIcon
            type={bookLookup.status === 1 ? 'success' : 'warning'}
            style={{ marginBottom: 16 }}
            message={`已匹配本站书籍：#${bookLookup.id}《${bookLookup.name}》`}
            description={`本站当前作者署名：${bookLookup.author || '未填写'}；当前书籍状态：${bookLookup.status === 1 ? '正常' : '已下架/异常'}`}
          />
        ) : null}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item
            name="author_real_name"
            label="作者真实姓名"
            rules={[{ required: true, message: '请输入作者真实姓名' }]}
            style={{ marginBottom: 12 }}
          >
            <Input placeholder="请输入作者真实姓名" />
          </Form.Item>
          <Form.Item
            name="author_pen_name"
            label="作者笔名"
            style={{ marginBottom: 12 }}
          >
            <Input placeholder="如本站署名为笔名，请填写" />
          </Form.Item>
          <Form.Item
            name="author_email"
            label="作者邮箱"
            rules={[
              { required: true, message: '请输入作者邮箱' },
              { type: 'email', message: '邮箱格式不正确' }
            ]}
            style={{ marginBottom: 12 }}
          >
            <Input placeholder="用于接收法务核验后的确认邮件" />
          </Form.Item>
          <Form.Item
            name="author_phone"
            label="作者电话"
            style={{ marginBottom: 12 }}
          >
            <Input placeholder="方便法务联系核实" />
          </Form.Item>
        </div>

        <Form.Item
          name="author_message"
          label="补充说明"
          style={{ marginBottom: 16 }}
        >
          <Input.TextArea rows={3} maxLength={1000} placeholder="可填写出版说明、署名情况、法务联系备注等" />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item label="1. 身份证明材料" required style={{ marginBottom: 12 }}>
            <Dragger {...buildUploadProps(identityFiles, setIdentityFiles)}>
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p>上传身份证正反面等身份证明</p>
              <p className="ant-upload-hint">支持 PDF / 图片 / Word / 压缩包，最多 5 个文件</p>
            </Dragger>
          </Form.Item>
          <Form.Item label="2. 作品原件/完整底稿" required style={{ marginBottom: 12 }}>
            <Dragger {...buildUploadProps(manuscriptFiles, setManuscriptFiles)}>
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p>上传电子稿、底稿、大纲、修改稿等</p>
              <p className="ant-upload-hint">支持 PDF / 图片 / Word / 压缩包，最多 5 个文件</p>
            </Dragger>
          </Form.Item>
          <Form.Item label="3. 出版物版权页信息" required style={{ marginBottom: 12 }}>
            <Dragger {...buildUploadProps(copyrightFiles, setCopyrightFiles)}>
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p>上传封面、扉页、版权页等信息</p>
              <p className="ant-upload-hint">支持 PDF / 图片 / Word / 压缩包，最多 5 个文件</p>
            </Dragger>
          </Form.Item>
          <Form.Item label="4. 出版/授权合同" required style={{ marginBottom: 12 }}>
            <Dragger {...buildUploadProps(contractFiles, setContractFiles)}>
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p>上传正式出版合同或授权合同</p>
              <p className="ant-upload-hint">支持 PDF / 图片 / Word / 压缩包，最多 5 个文件</p>
            </Dragger>
          </Form.Item>
        </div>
      </Form>
    </Modal>
  )
}

export default AuthorClaimModal
